import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import StatusContainer from '@/components/StatusContainer';
import { getCatalogPalette } from '@/components/Catalog/catalogPalette';

const STEPS = [
  'pages.agents.intro.step1',
  'pages.agents.intro.step2',
  'pages.agents.intro.step3',
];

export default function BotsPersonalSection({ hasUser, count, isLoading, error, onRetry, onCreate, children }) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();
  const palette = getCatalogPalette(theme);
  const styles = useMemo(() => createStyles(palette, isXS), [palette, isXS]);
  const hasBots = hasUser && count > 0;

  return (
    <View style={styles.section} testID="bots-personal-section">
      <View style={styles.heading}>
        <View style={styles.title}>
          <Text tag="h2" size="l" weight="semibold">{t('pages.agents.myAgents')}</Text>
          {hasBots && <Text size="s" color="tertiary">{count}</Text>}
        </View>
        <Button
          text={t('pages.agents.intro.createCta')}
          prependIcon="plus"
          variant="secondary"
          size="small"
          textWeight="semibold"
          onPress={onCreate}
          style={{ backgroundColor: palette.ink, borderRadius: 10, minHeight: 36 }}
          textStyle={{ color: palette.onInk }}
          iconColor={palette.onInk}
          hoverBackgroundColor={palette.ink}
        />
      </View>
      {hasUser && error ? (
        <StatusContainer type="error" message={error} actionText={t('pages.common.retry')} onAction={onRetry} />
      ) : hasUser && (isLoading || hasBots) ? children : (
        <View style={styles.empty}>
          <View style={styles.emptyIntro}>
            <View style={styles.icon}><Icon name="robot" size={20} color={palette.icon} /></View>
            <View style={styles.emptyCopy}>
              <Text size="s" weight="semibold">{t('pages.agents.intro.createTitle')}</Text>
              <Text size="s" color="secondary" style={styles.body}>{t('pages.agents.noAgentsYet')}</Text>
            </View>
          </View>
          <View style={styles.steps}>
            {STEPS.map((step, index) => (
              <View key={step} style={styles.step}>
                <View style={styles.stepIcon}>
                  <Text size="xxs" weight="semibold" color="secondary">{index + 1}</Text>
                </View>
                <Text size="xs" color="secondary" style={styles.stepText}>{t(step)}</Text>
              </View>
            ))}
          </View>
          {!hasUser && <Text size="xs" color="tertiary" style={styles.hint}>{t('pages.agents.intro.signInHint')}</Text>}
        </View>
      )}
    </View>
  );
}

const createStyles = (palette, compact) => StyleSheet.create({
  section: { gap: 12 },
  heading: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 16 },
  title: { flexDirection: 'row', alignItems: 'center', gap: 10, flexShrink: 1 },
  empty: {
    borderWidth: 1,
    borderColor: palette.border,
    backgroundColor: palette.surface,
    borderRadius: 16,
    padding: 18,
    gap: 14,
  },
  emptyIntro: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  icon: {
    width: 40,
    height: 40,
    borderRadius: 11,
    backgroundColor: palette.preview,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyCopy: { flex: 1, minWidth: 0, gap: 4 },
  body: { lineHeight: 20, maxWidth: 620 },
  steps: { flexDirection: compact ? 'column' : 'row', gap: compact ? 10 : 12 },
  step: {
    flex: compact ? undefined : 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  stepIcon: {
    width: 20,
    height: 20,
    borderRadius: 10,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: palette.preview,
  },
  stepText: { flex: 1, minWidth: 0, lineHeight: 20 },
  hint: { flexShrink: 1 },
});
